import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Shared shell for the public legal pages (/privacy, /terms, /data-deletion).
 *
 * These pages are intentionally **un-authenticated** so the Meta App Review
 * crawler and any external reviewer can hit them without logging in. The
 * layout keeps a slim header (logo + Back link) and a generous reading
 * container so long copy stays scannable.
 */
export default function LegalShell({ title, subtitle, lastUpdated, children, testidPrefix = "legal" }) {
  return (
    <div
      className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)]"
      data-testid={`${testidPrefix}-shell`}
    >
      <header className="border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 font-display text-base font-semibold" data-testid={`${testidPrefix}-home-link`}>
            <span className="h-7 w-7 rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] flex items-center justify-center text-[color:var(--bg-0)]">
              <Sparkles className="h-4 w-4" />
            </span>
            Postmoro
          </Link>
          <Link to="/">
            <Button
              size="sm"
              variant="outline"
              className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
              data-testid={`${testidPrefix}-back`}
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
              Back to homepage
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">Legal</div>
          <h1
            className="font-display text-3xl md:text-4xl font-semibold mt-1 leading-tight"
            data-testid={`${testidPrefix}-title`}
          >
            {title}
          </h1>
          {subtitle ? (
            <p className="text-[color:var(--text-2)] mt-2 leading-relaxed">{subtitle}</p>
          ) : null}
          {lastUpdated ? (
            <p className="text-xs text-[color:var(--text-3)] mt-3">Last updated: {lastUpdated}</p>
          ) : null}
        </div>

        <article className="prose prose-invert max-w-none text-[color:var(--text-2)] leading-relaxed [&_h2]:font-display [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-[color:var(--text-1)] [&_h3]:font-display [&_h3]:text-base [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-[color:var(--text-1)] [&_p]:my-3 [&_ul]:my-3 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:my-1 [&_a]:text-[color:var(--brand-primary)] [&_a:hover]:underline [&_strong]:text-[color:var(--text-1)]">
          {children}
        </article>
      </main>

      <footer className="border-t border-[color:var(--border-subtle)] mt-12">
        <div className="max-w-3xl mx-auto px-6 py-6 flex flex-wrap items-center gap-4 text-xs text-[color:var(--text-3)]">
          <span>© {new Date().getFullYear()} Postmoro LLC. All rights reserved.</span>
          <Link to="/privacy" className="hover:text-[color:var(--text-1)]" data-testid={`${testidPrefix}-footer-privacy`}>Privacy</Link>
          <Link to="/terms" className="hover:text-[color:var(--text-1)]" data-testid={`${testidPrefix}-footer-terms`}>Terms</Link>
          <Link to="/data-deletion" className="hover:text-[color:var(--text-1)]" data-testid={`${testidPrefix}-footer-data`}>Data Deletion</Link>
          <a href="mailto:support@postmoro.com" className="hover:text-[color:var(--text-1)]" data-testid={`${testidPrefix}-footer-contact`}>Contact</a>
        </div>
      </footer>
    </div>
  );
}
